import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { templatesAPI, submissionsAPI, vehiclesAPI, driversAPI, depotsAPI, uploadFile } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { 
  ArrowLeft,
  Save,
  Send,
  Camera,
  Upload,
  Check,
  X,
  AlertCircle,
  Loader2,
  Trash2,
  Plus,
  Minus
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';

// Signature Pad Component
const SignaturePad = ({ onSave, existingSignature }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(!!existingSignature);

  useEffect(() => {
    if (existingSignature && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0);
      };
      img.src = existingSignature;
    }
  }, [existingSignature]);

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#0F172A';
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    setHasSignature(true);
    if (onSave) {
      onSave(canvasRef.current.toDataURL());
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    if (onSave) {
      onSave(null);
    }
  };

  return (
    <div className="space-y-2">
      <canvas
        ref={canvasRef}
        width={300}
        height={150}
        className={`signature-pad w-full border-2 ${hasSignature ? 'border-emerald-500 bg-emerald-50' : 'border-dashed border-slate-300 bg-slate-50'}`}
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
        onTouchStart={startDrawing}
        onTouchMove={draw}
        onTouchEnd={stopDrawing}
      />
      {hasSignature && (
        <Button variant="outline" size="sm" onClick={clearSignature}>
          <Trash2 className="w-4 h-4 mr-2" />
          Clear
        </Button>
      )}
    </div>
  );
};

// Inspection Item Component
const InspectionItem = ({ item, index, onStatusChange, onCommentChange }) => {
  return (
    <div className="inspection-item" data-testid={`inspection-item-${index}`}>
      <span className="inspection-item-name">{item.item_name}</span>
      <div className="inspection-item-status">
        <Button
          type="button"
          size="sm"
          variant={item.status === 'OK' ? 'default' : 'outline'}
          className={item.status === 'OK' ? 'bg-emerald-500 hover:bg-emerald-600' : ''}
          onClick={() => onStatusChange(index, 'OK')}
          data-testid={`item-${index}-ok`}
        >
          <Check className="w-4 h-4" />
        </Button>
        <Button
          type="button"
          size="sm"
          variant={item.status === 'Problem' ? 'default' : 'outline'}
          className={item.status === 'Problem' ? 'bg-red-500 hover:bg-red-600' : ''}
          onClick={() => onStatusChange(index, 'Problem')}
          data-testid={`item-${index}-problem`}
        >
          <X className="w-4 h-4" />
        </Button>
        <Button
          type="button"
          size="sm"
          variant={item.status === 'N/A' ? 'default' : 'outline'}
          className={item.status === 'N/A' ? 'bg-slate-500 hover:bg-slate-600' : ''}
          onClick={() => onStatusChange(index, 'N/A')}
          data-testid={`item-${index}-na`}
        >
          N/A
        </Button>
      </div>
      {item.status === 'Problem' && (
        <div className="w-full mt-2">
          <Input
            placeholder="Describe the problem..."
            value={item.comment || ''}
            onChange={(e) => onCommentChange(index, e.target.value)}
            className="w-full"
            data-testid={`item-${index}-comment`}
          />
        </div>
      )}
    </div>
  );
};

export const NewSubmissionPage = () => {
  const { templateCode } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  const [vehicles, setVehicles] = useState([]);
  const [trailers, setTrailers] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [depots, setDepots] = useState([]);
  
  const [formData, setFormData] = useState({});
  const [lineItems, setLineItems] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState('');
  const [selectedTrailer1, setSelectedTrailer1] = useState('');
  const [selectedTrailer2, setSelectedTrailer2] = useState('');
  const [selectedDepot, setSelectedDepot] = useState('');
  const [driverSignature, setDriverSignature] = useState(null);
  const [attachments, setAttachments] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch template
        const templateResponse = await templatesAPI.get(templateCode);
        setTemplate(templateResponse.data);
        
        // Initialize form data with defaults
        const initialFormData = {};
        templateResponse.data.fields?.forEach(field => {
          if (field.default_value !== undefined) {
            initialFormData[field.name] = field.default_value;
          }
        });
        setFormData(initialFormData);
        
        // Fetch related data
        const [vehiclesRes, depotsRes, driversRes] = await Promise.all([
          vehiclesAPI.list(),
          depotsAPI.list(),
          driversAPI.list(),
        ]);
        
        setVehicles(vehiclesRes.data || []);
        setDepots(depotsRes.data || []);
        setDrivers(driversRes.data || []);
        
        // Filter trailers (vehicle_type contains 'Trailer')
        const trailerList = (vehiclesRes.data || []).filter(v => 
          v.vehicle_type?.toLowerCase().includes('trailer')
        );
        setTrailers(trailerList);
        
        // Set default depot if user has one
        if (user?.depot_id) {
          setSelectedDepot(user.depot_id);
        } else if (depotsRes.data?.length > 0) {
          setSelectedDepot(depotsRes.data[0].id);
        }
        
      } catch (err) {
        console.error('Failed to fetch template:', err);
        setError('Failed to load template. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [templateCode, user]);

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
  };

  const handleLineItemStatusChange = (index, status) => {
    setLineItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], status };
      return updated;
    });
  };

  const handleLineItemCommentChange = (index, comment) => {
    setLineItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], comment };
      return updated;
    });
  };

  const handleFileUpload = async (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const result = await uploadFile(file);
      handleFieldChange(fieldName, result.file_url);
      toast.success('File uploaded successfully');
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to upload file');
    }
  };

  const handleSaveDraft = async () => {
    try {
      setSaving(true);
      
      const submissionData = {
        template_id: template.id,
        vehicle_id: selectedVehicle || null,
        trailer_ids: [selectedTrailer1, selectedTrailer2].filter(Boolean),
        depot_id: selectedDepot,
        form_data: formData,
        line_items: lineItems.map(item => ({
          id: item.id || crypto.randomUUID(),
          item_name: item.item_name,
          status: item.status,
          comment: item.comment || '',
          data: {}
        })),
      };
      
      const response = await submissionsAPI.create(submissionData);
      toast.success('Draft saved successfully');
      navigate(`/submissions/${response.data.id}`);
      
    } catch (err) {
      console.error('Failed to save draft:', err);
      toast.error('Failed to save draft. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    // Validate required fields
    const hasProblems = lineItems.some(item => item.status === 'Problem');
    const allItemsChecked = lineItems.every(item => item.status);
    
    if (templateCode === 'F004' && !allItemsChecked) {
      toast.error('Please check all inspection items before submitting');
      return;
    }
    
    if (template?.requires_driver_signature && !driverSignature) {
      toast.error('Driver signature is required');
      return;
    }

    try {
      setSubmitting(true);
      
      // First save the submission
      const submissionData = {
        template_id: template.id,
        vehicle_id: selectedVehicle || null,
        trailer_ids: [selectedTrailer1, selectedTrailer2].filter(Boolean),
        depot_id: selectedDepot,
        form_data: formData,
        line_items: lineItems.map(item => ({
          id: item.id || crypto.randomUUID(),
          item_name: item.item_name,
          status: item.status,
          comment: item.comment || '',
          data: {}
        })),
      };
      
      const createResponse = await submissionsAPI.create(submissionData);
      const submissionId = createResponse.data.id;
      
      // Update with signature if provided
      if (driverSignature) {
        await submissionsAPI.update(submissionId, {
          driver_signature: driverSignature,
          has_problems: hasProblems,
        });
      }
      
      // Submit for approval
      await submissionsAPI.submit(submissionId);
      
      toast.success('Submission sent for approval');
      navigate(`/submissions/${submissionId}`);
      
    } catch (err) {
      console.error('Failed to submit:', err);
      toast.error('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field) => {
    // Section headers handled in main render
    if (field.field_type === 'section') {
      return null;
    }

    // Handle vehicle select dropdown
    if (field.field_type === 'vehicle_select') {
      const vehicleList = field.name.includes('trailer') ? trailers : vehicles.filter(v => !v.vehicle_type?.toLowerCase().includes('trailer'));
      return (
        <Select 
          value={formData[field.name] || ''} 
          onValueChange={(value) => handleFieldChange(field.name, value)}
        >
          <SelectTrigger className="h-12" data-testid={`field-${field.name}`}>
            <SelectValue placeholder={`Select ${field.label}`} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">-- None --</SelectItem>
            {vehicleList.map(v => (
              <SelectItem key={v.id} value={v.registration_number}>
                {v.registration_number} - {v.make} {v.model}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    // Handle driver select dropdown
    if (field.field_type === 'driver_select') {
      return (
        <Select 
          value={formData[field.name] || ''} 
          onValueChange={(value) => handleFieldChange(field.name, value)}
        >
          <SelectTrigger className="h-12" data-testid={`field-${field.name}`}>
            <SelectValue placeholder="Select Driver" />
          </SelectTrigger>
          <SelectContent>
            {drivers.map(d => {
              const name = (d.first_name || d.user?.first_name || '') + ' ' + (d.last_name || d.user?.last_name || '');
              return (
                <SelectItem key={d.id} value={d.id}>
                  {name.trim() || d.employee_number || d.id}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      );
    }

    // Handle inspection check (In Order / Needs Attention)
    if (field.field_type === 'inspection_check') {
      const value = formData[field.name] || '';
      const needsAttention = value === 'Needs Attention';
      
      return (
        <div className="space-y-2">
          <div className="flex items-center gap-4">
            <RadioGroup
              value={value}
              onValueChange={(val) => handleFieldChange(field.name, val)}
              className="flex gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="In Order" id={`${field.name}-ok`} />
                <Label htmlFor={`${field.name}-ok`} className="text-sm font-normal text-emerald-600">
                  In Order
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="Needs Attention" id={`${field.name}-na`} />
                <Label htmlFor={`${field.name}-na`} className="text-sm font-normal text-red-600">
                  Needs Attention
                </Label>
              </div>
            </RadioGroup>
          </div>
          {needsAttention && (
            <Input
              placeholder="Describe the problem..."
              value={formData[`${field.name}_comment`] || ''}
              onChange={(e) => handleFieldChange(`${field.name}_comment`, e.target.value)}
              className="mt-2 border-red-300"
              data-testid={`${field.name}-comment`}
            />
          )}
        </div>
      );
    }

    // Handle Yes/No questions
    if (field.field_type === 'yes_no') {
      const value = formData[field.name] || '';
      const expectedAnswer = field.expected_answer;
      const isProblematic = (expectedAnswer === 'yes_required' && value === 'No') ||
                           (expectedAnswer === 'no_required' && value === 'Yes');
      
      return (
        <div className="space-y-2">
          <RadioGroup
            value={value}
            onValueChange={(val) => handleFieldChange(field.name, val)}
            className="flex gap-4"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="Yes" id={`${field.name}-yes`} />
              <Label htmlFor={`${field.name}-yes`} className="text-sm font-normal">Yes</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="No" id={`${field.name}-no`} />
              <Label htmlFor={`${field.name}-no`} className="text-sm font-normal">No</Label>
            </div>
          </RadioGroup>
          {isProblematic && (
            <Input
              placeholder="Please explain..."
              value={formData[`${field.name}_comment`] || ''}
              onChange={(e) => handleFieldChange(`${field.name}_comment`, e.target.value)}
              className="mt-2 border-red-300"
              data-testid={`${field.name}-comment`}
            />
          )}
        </div>
      );
    }

    switch (field.field_type) {
      case 'text':
        return (
          <Input
            value={formData[field.name] || ''}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            placeholder={field.label}
            required={field.required}
            className="h-12"
            data-testid={`field-${field.name}`}
          />
        );
      
      case 'textarea':
        return (
          <Textarea
            value={formData[field.name] || ''}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            placeholder={field.label}
            required={field.required}
            rows={4}
            data-testid={`field-${field.name}`}
          />
        );
      
      case 'number':
        return (
          <Input
            type="number"
            value={formData[field.name] || ''}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            placeholder={field.label}
            required={field.required}
            className="h-12 font-mono"
            data-testid={`field-${field.name}`}
          />
        );
      
      case 'date':
        return (
          <Input
            type="date"
            value={formData[field.name] || ''}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            required={field.required}
            className="h-12"
            data-testid={`field-${field.name}`}
          />
        );
      
      case 'select':
        // Special handling for vehicle and driver selects
        if (field.name === 'vehicle_id') {
          return (
            <Select value={selectedVehicle} onValueChange={setSelectedVehicle}>
              <SelectTrigger className="h-12" data-testid={`field-${field.name}`}>
                <SelectValue placeholder="Select vehicle" />
              </SelectTrigger>
              <SelectContent>
                {vehicles.map(v => (
                  <SelectItem key={v.id} value={v.id}>
                    {v.registration_number} - {v.make} {v.model}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          );
        }
        
        return (
          <Select 
            value={formData[field.name] || ''} 
            onValueChange={(value) => handleFieldChange(field.name, value)}
          >
            <SelectTrigger className="h-12" data-testid={`field-${field.name}`}>
              <SelectValue placeholder={`Select ${field.label}`} />
            </SelectTrigger>
            <SelectContent>
              {field.options?.map(option => (
                <SelectItem key={option} value={option}>{option}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      
      case 'checkbox':
        return (
          <div className="flex items-center space-x-2">
            <Checkbox
              id={field.name}
              checked={formData[field.name] || false}
              onCheckedChange={(checked) => handleFieldChange(field.name, checked)}
              data-testid={`field-${field.name}`}
            />
            <Label htmlFor={field.name} className="text-sm font-normal">
              {field.label}
            </Label>
          </div>
        );
      
      case 'file':
      case 'photo':
        return (
          <div className="space-y-2">
            <input
              type="file"
              accept={field.field_type === 'photo' ? 'image/*' : '*'}
              capture={field.field_type === 'photo' ? 'environment' : undefined}
              onChange={(e) => handleFileUpload(e, field.name)}
              className="hidden"
              id={`file-${field.name}`}
              data-testid={`field-${field.name}`}
            />
            <label
              htmlFor={`file-${field.name}`}
              className="flex items-center justify-center gap-2 h-12 px-4 border-2 border-dashed border-slate-300 rounded-sm cursor-pointer hover:border-slate-400 transition-colors"
            >
              {field.field_type === 'photo' ? (
                <Camera className="w-5 h-5 text-slate-400" />
              ) : (
                <Upload className="w-5 h-5 text-slate-400" />
              )}
              <span className="text-sm text-slate-500">
                {formData[field.name] ? 'File uploaded' : `Upload ${field.label}`}
              </span>
            </label>
            {formData[field.name] && (
              <p className="text-sm text-emerald-600 flex items-center gap-1">
                <Check className="w-4 h-4" />
                File uploaded
              </p>
            )}
          </div>
        );
      
      default:
        return (
          <Input
            value={formData[field.name] || ''}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            placeholder={field.label}
            className="h-12"
            data-testid={`field-${field.name}`}
          />
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <p className="text-slate-600">{error}</p>
        <Button onClick={() => navigate('/templates')} className="mt-4">
          Back to Templates
        </Button>
      </div>
    );
  }

  // Separate line items field from regular fields
  const regularFields = template?.fields?.filter(f => f.field_type !== 'line_items') || [];
  const hasLineItems = template?.fields?.some(f => f.field_type === 'line_items') || templateCode === 'F004';

  return (
    <div className="space-y-6 pb-32 md:pb-6 animate-fade-in" data-testid="new-submission-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={() => navigate(-1)}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="font-mono">{template?.code}</Badge>
            <Badge className="badge-neutral">New</Badge>
          </div>
          <h1 
            className="text-2xl font-bold text-slate-900 mt-1"
            style={{ fontFamily: 'Barlow Condensed' }}
          >
            {template?.name}
          </h1>
        </div>
      </div>

      {/* Vehicle & Depot Selection */}
      <Card className="form-section">
        <CardHeader className="pb-4">
          <CardTitle className="form-section-title">Assignment</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Vehicle</Label>
              <Select value={selectedVehicle} onValueChange={setSelectedVehicle}>
                <SelectTrigger className="h-12" data-testid="select-vehicle">
                  <SelectValue placeholder="Select vehicle" />
                </SelectTrigger>
                <SelectContent>
                  {vehicles.map(v => (
                    <SelectItem key={v.id} value={v.id}>
                      <span className="font-mono">{v.registration_number}</span>
                      <span className="text-slate-500 ml-2">{v.make} {v.model}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Depot</Label>
              <Select value={selectedDepot} onValueChange={setSelectedDepot}>
                <SelectTrigger className="h-12" data-testid="select-depot">
                  <SelectValue placeholder="Select depot" />
                </SelectTrigger>
                <SelectContent>
                  {depots.map(d => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.name} ({d.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Form Fields */}
      {regularFields.length > 0 && (
        <Card className="form-section">
          <CardHeader className="pb-4">
            <CardTitle className="form-section-title">Form Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
              {regularFields.map(field => {
                // Section headers get full width and no label wrapper
                if (field.field_type === 'section') {
                  return (
                    <div 
                      key={field.name}
                      className="col-span-full mt-4 mb-2 border-b-2 border-slate-300 pb-2"
                    >
                      <h3 
                        className="text-lg font-bold text-slate-800 uppercase tracking-wide"
                        style={{ fontFamily: 'Barlow Condensed' }}
                      >
                        {field.label}
                      </h3>
                    </div>
                  );
                }
                
                // Textarea and inspection items get appropriate widths
                const isWide = field.field_type === 'textarea';
                const isInspectionCheck = field.field_type === 'inspection_check' || field.field_type === 'yes_no';
                
                return (
                  <div 
                    key={field.name} 
                    className={`space-y-2 ${isWide ? 'col-span-full' : ''}`}
                  >
                    <Label className={isInspectionCheck ? 'font-medium' : ''}>
                      {field.label}
                      {field.required && <span className="text-red-500 ml-1">*</span>}
                    </Label>
                    {renderField(field)}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Inspection Items for F004 */}
      {hasLineItems && lineItems.length > 0 && (
        <Card className="form-section">
          <CardHeader className="pb-4">
            <CardTitle className="form-section-title">
              Inspection Checklist
              <span className="ml-2 text-sm font-normal text-slate-500">
                ({lineItems.filter(i => i.status).length}/{lineItems.length} checked)
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1">
              {lineItems.map((item, index) => (
                <InspectionItem
                  key={index}
                  item={item}
                  index={index}
                  onStatusChange={handleLineItemStatusChange}
                  onCommentChange={handleLineItemCommentChange}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Signature Section */}
      {template?.requires_driver_signature && (
        <Card className="form-section">
          <CardHeader className="pb-4">
            <CardTitle className="form-section-title">
              Driver Signature
              <span className="text-red-500 ml-1">*</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-500 mb-4">
              I confirm that the information provided is accurate and complete.
            </p>
            <SignaturePad 
              onSave={setDriverSignature} 
              existingSignature={driverSignature}
            />
          </CardContent>
        </Card>
      )}

      {/* Action Buttons - Sticky on mobile */}
      <div className="sticky-action-bar">
        <Button 
          variant="outline" 
          onClick={handleSaveDraft}
          disabled={saving || submitting}
          className="flex-1 md:flex-none h-12"
          data-testid="save-draft-btn"
        >
          {saving ? (
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
          ) : (
            <Save className="w-5 h-5 mr-2" />
          )}
          Save Draft
        </Button>
        <Button 
          onClick={handleSubmit}
          disabled={saving || submitting}
          className="flex-1 md:flex-none h-12 bg-orange-500 hover:bg-orange-600"
          data-testid="submit-btn"
        >
          {submitting ? (
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
          ) : (
            <Send className="w-5 h-5 mr-2" />
          )}
          Submit
        </Button>
      </div>
    </div>
  );
};

export default NewSubmissionPage;
